import { z } from "zod";
import { AdminActionType, AnswerConfidence, AttemptMediaKind, AttemptStage, AttemptStatus, DeviceRole, Difficulty, ExamStatus, ExamTimingMode, ProctorEventType, QuestionSource, QuestionStatus, QuestionType, RecordingSource, UserRole, } from "./enums.js";
import { CodeTest, HttpUrl, Answer, AnswerResponse, Attempt, AttemptMedia, CorrectAnswer, DeviceSession, EpochMs, Exam, ExamQuestion, Id, ProctorEvent, Question, QuestionOption, Recording, StudentQuestion, Subject, Topic, VisitOffsets, } from "./schemas.js";
/**
 * Request/response contracts for every non-auth endpoint. One schema per wire
 * shape, named `<Thing>Input` for request bodies and `<Thing>Response` for
 * responses, so `docs/API.md` and the code cannot drift apart.
 *
 * Rules encoded here:
 *  - Anything a student can receive mid-attempt is built from `StudentQuestion`
 *    (no `correct_answer`, no `solution_md`).
 *  - Every timestamp is epoch **milliseconds, UTC**.
 *  - Query params arrive as strings; the `*Query` schemas coerce.
 */
/* ------------------------------------------------------------------ */
/* shared helpers                                                      */
/* ------------------------------------------------------------------ */
export const OkResponse = z.object({ ok: z.literal(true) });
/** Every list endpoint returns its rows plus the unfiltered-page total. */
export const PageQuery = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
/* ------------------------------------------------------------------ */
/* subjects & topics (admin)                                           */
/* ------------------------------------------------------------------ */
export const SubjectsResponse = z.object({ subjects: z.array(Subject) });
export const SubjectResponse = z.object({ subject: Subject });
export const TopicsQuery = z.object({ subject_id: Id.optional() });
export const TopicsResponse = z.object({ topics: z.array(Topic) });
export const TopicResponse = z.object({ topic: Topic });
/* ------------------------------------------------------------------ */
/* questions (admin)                                                   */
/* ------------------------------------------------------------------ */
/**
 * Every question the API returns carries its subject/topic/week denormalised,
 * so no list screen has to join client-side or fire a lookup per row.
 */
export const QuestionWithContext = Question.extend({
    subject_id: Id,
    subject_name: z.string(),
    subject_slug: z.string(),
    topic_name: z.string(),
    week_no: z.number().int().nullable(),
    /**
     * The TOPIC's lesson link, carried alongside the question's own. The result
     * page falls back to it for a wrong answer whose question has no link, so
     * one video per topic covers a whole paper.
     */
    topic_video_url: HttpUrl.nullable().optional(),
    /**
     * Signed, short-lived URL for the stored figure — null when the question has
     * none. Without this an admin would be approving a question whose figure they
     * cannot look at, which defeats the review.
     */
    figure_url: z.string().nullable(),
});
/** Admin figure URLs are short-lived; the screens that use them are live. */
export const FIGURE_URL_TTL_S = 15 * 60;
/** Accepted image types for a question figure. */
export const FIGURE_CONTENT_TYPES = [
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/gif",
];
export const MAX_FIGURE_BYTES = 8 * 1024 * 1024;
export const UploadFigureResponse = z.object({
    question_id: Id,
    figure_r2_key: z.string(),
    figure_url: z.string(),
    bytes: z.number().int().nonnegative(),
    /** True when this replaced a figure the question already had. */
    replaced: z.boolean(),
});
export const DeleteFigureResponse = z.object({
    question_id: Id,
    /** Removing the asset re-opens the gate, so an approved question is demoted. */
    status: QuestionStatus,
    requires_figure: z.boolean(),
});
export const QuestionListQuery = PageQuery.extend({
    topic_id: Id.optional(),
    subject_id: Id.optional(),
    /** Filters on the topic's week — server-side, not per page on the client. */
    week_no: z.coerce.number().int().min(1).max(52).optional(),
    type: QuestionType.optional(),
    difficulty: Difficulty.optional(),
    status: QuestionStatus.optional(),
    source: QuestionSource.optional(),
    /** Substring match on body_md. */
    q: z.string().min(1).max(200).optional(),
});
export const QuestionsResponse = z.object({
    questions: z.array(QuestionWithContext),
    total: z.number().int().nonnegative(),
});
export const QuestionResponse = z.object({ question: QuestionWithContext });
/**
 * Why a question needs a human's eyes. Computed server-side so the review queue
 * can show the reason at a glance instead of the admin inferring it from
 * three separate columns.
 */
export const QuestionReviewFlags = z.object({
    /** Answer key not yet confirmed by a human. Blocks NAT attachment. */
    unverified_key: z.boolean(),
    /** The source marked its own answer as uncertain. */
    low_confidence: z.boolean(),
    /**
     * The question refers to a figure that is not stored — the student would see
     * a question they cannot answer. Excluded from auto-pick and from attach.
     */
    missing_figure: z.boolean(),
    /**
     * A numeric key from a source known to truncate rather than round, so the
     * published value may be short by up to one unit in the last place.
     */
    numeric_key_may_be_truncated: z.boolean(),
});
/** A queue row: the question plus why it is here. */
export const ReviewQueueItem = z.object({
    question: QuestionWithContext,
    flags: QuestionReviewFlags,
    /** True when any flag is set — the one field a list view needs. */
    needs_attention: z.boolean(),
    /** Ordered, human-readable reasons, ready to render as chips. */
    reasons: z.array(z.string()),
});
export const ReviewQueueResponse = z.object({
    questions: z.array(ReviewQueueItem),
    total: z.number().int().nonnegative(),
    /** Counts across the whole queue, not just this page. */
    summary: z.object({
        pending: z.number().int().nonnegative(),
        unverified_key: z.number().int().nonnegative(),
        missing_figure: z.number().int().nonnegative(),
        low_confidence: z.number().int().nonnegative(),
    }),
});
/**
 * Create/replace payload. `status` and `source` default server-side rather than
 * being required, so the admin UI can post a minimal body.
 */
export const CreateQuestionInput = z.object({
    topic_id: Id,
    external_id: z.string().min(1).max(120).nullable().default(null),
    type: QuestionType,
    body_md: z.string().min(1),
    options: z.array(QuestionOption).default([]),
    correct_answer: CorrectAnswer,
    solution_md: z.string().nullable().default(null),
    /**
     * Where to send a student who gets this wrong. Shown on the result page
     * only — during the exam it would be an answer key with extra steps.
     *
     * `""` is accepted so an emptied input in the editor clears the link
     * instead of failing validation on a field the author is trying to remove.
     */
    video_url: z.union([HttpUrl, z.literal("")]).nullable().default(null),
    reading_url: z.union([HttpUrl, z.literal("")]).nullable().default(null),
    starter_code: z.string().max(20_000).nullable().default(null),
    tests: z.array(CodeTest).max(50).nullable().default(null),
    difficulty: Difficulty,
    source: QuestionSource.default("manual"),
    /** Everything authored or imported starts in the review queue. */
    status: QuestionStatus.default("pending"),
    marks: z.number().positive().max(100).default(1),
    confidence: AnswerConfidence.nullable().default(null),
    source_url: z.string().max(500).nullable().default(null),
    /** A hand-authored question is verified by the act of writing it. */
    key_verified: z.boolean().default(true),
    import_source: z.string().max(200).nullable().default(null),
    requires_figure: z.boolean().default(false),
    figure_r2_key: z.string().max(300).nullable().default(null),
});
export const UpdateQuestionInput = CreateQuestionInput.partial().refine((v) => Object.keys(v).length > 0, { message: "At least one field is required" });
export const ReviewQuestionInput = z.object({
    /**
     * `withdraw` puts an APPROVED question back into the review queue.
     *
     * It exists so item analysis can do something about what it finds. Reporting
     * that a question's answer key is probably wrong and then leaving it in the
     * pool for the next auto-picked paper is half a feature: the same broken
     * question goes out again, to a different cohort, and takes another mark off
     * everyone who understood it.
     *
     * Distinct from `reject`, which is a judgement that the question is bad.
     * Withdrawing says only "the data says look at this again" — the question
     * may well be fine and the key wrong, which is a fix rather than a deletion.
     *
     * Papers that already contain it are NOT changed. Altering a paper people
     * have already sat is never the right response to noticing a problem with it.
     */
    action: z.enum(["approve", "reject", "withdraw"]),
    /**
     * Confirms the admin has checked the answer key against the question.
     * Approving a NAT question whose key is still unverified requires this — the
     * upstream numeric keys are truncated, so approval must be a deliberate act,
     * not a side effect of clicking through the queue.
     */
    key_verified: z.boolean().optional(),
    /** Correct the figure flags while reviewing. */
    requires_figure: z.boolean().optional(),
    figure_r2_key: z.string().max(300).nullable().optional(),
});
/* --- bulk import: the on-disk content pack format (content/*.json) ------- */
/**
 * A question exactly as it appears in `content/pyq/*.json` and
 * `content/generated/*.json` — see `content/README.md` §5. This is deliberately
 * *their* shape, not the DB shape: options are plain strings (the option key is
 * its letter, A, B, C…), difficulty says "medium" rather than "med", and the
 * topic is a human-readable path. The importer does the translation.
 */
export const ContentPackQuestion = z.object({
    /** Stable id from the content file — stored as `questions.external_id`. */
    id: z.string().min(1).max(120),
    body_md: z.string().min(1),
    type: QuestionType,
    /**
     * Option texts in order; `options[i]` is given the key `OPTION_KEYS[i]`.
     * NAT questions write `null` here as often as they omit the field, so both
     * mean "no options".
     */
    options: z
        .array(z.string())
        .nullish()
        .transform((options) => options ?? []),
    /** MCQ: "B" · MSQ: ["A","C"] · NAT: a number. Keys, never indices. */
    correct_answer: z.union([
        z.string().min(1),
        z.array(z.string().min(1)).min(1),
        z.number(),
    ]),
    /**
     * NAT only. Absent means: exact for an integer answer, 0.01 for a decimal —
     * a guard against float representation, not against student rounding.
     */
    tolerance: z.number().nonnegative().optional(),
    solution_md: z.string().nullable().default(null),
    /** ["Set Theory", "Sets and their operations"] — topic, then subtopic. */
    topic_path: z.array(z.string().min(1)).min(1),
    week_no: z.number().int().min(1).max(52).nullable().default(null),
    difficulty: Difficulty,
    marks: z.number().positive().max(100).default(1),
    source: QuestionSource.default("generated"),
    source_url: z.string().nullable().default(null),
    confidence: AnswerConfidence.default("low"),
    /**
     * The question refers to a figure. When the pack does not say, the importer
     * infers it from a Markdown image in `body_md`.
     */
    requires_figure: z.boolean().optional(),
    /** URL of the figure in the source. Usually dead — recorded, not fetched. */
    figure_url: z.string().nullable().optional(),
    /**
     * Set by the content worker when it has confirmed the key itself. Absent or
     * false means the key came from an upstream source and is not trusted.
     */
    key_verified: z.boolean().optional(),
});
/** One `content/pyq/<slug>.json` or `content/generated/<slug>.json` file. */
export const ContentPackFile = z.object({
    subject: z.string().min(1),
    slug: z
        .string()
        .min(1)
        .regex(/^[a-z0-9-]+$/),
    level: z.string().optional(),
    source: QuestionSource.default("generated"),
    questions: z.array(ContentPackQuestion).max(1000).default([]),
});
export const ImportQuestionsInput = z.object({
    file: ContentPackFile,
    /** Create the subject and any missing topics/subtopics instead of failing. */
    create_missing_topics: z.boolean().default(true),
    /** Preview only: validate and report, write nothing. */
    dry_run: z.boolean().default(false),
});
export const ImportQuestionsResponse = z.object({
    created: z.number().int().nonnegative(),
    /** Matched an existing `external_id` and were updated in place. */
    updated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    /** Landed pending with an unverified answer key — a human must check these. */
    needs_key_check: z.number().int().nonnegative(),
    /** Reference a figure that was not stored, so they cannot go on a paper. */
    missing_figure: z.number().int().nonnegative(),
    dry_run: z.boolean(),
    subject_id: Id,
    created_ids: z.array(Id),
    /**
     * Questions whose `topic_path` did not fully resolve against the imported
     * syllabus. The question is still imported, attached to the deepest ancestor
     * that *did* resolve — never to an invented topic, and never dropped — so a
     * typo in a subtopic label surfaces here instead of quietly forking the topic
     * tree and skewing the per-topic weakness reporting.
     */
    unresolved_topics: z.array(z.object({
        external_id: z.string(),
        topic_path: z.array(z.string()),
        /** The part of `topic_path` that matched, joined with " > ". */
        resolved_to: z.string(),
        /** The first element that did not match. */
        unresolved_from: z.string(),
    })),
    errors: z.array(z.object({
        index: z.number().int(),
        external_id: z.string().nullable(),
        message: z.string(),
    })),
});
/* --- syllabus import: content/syllabus/*.json --------------------------- */
/** One `content/syllabus/<slug>.json` file — the subject + topic tree. */
export const SyllabusFile = z.object({
    subject: z.string().min(1),
    slug: z
        .string()
        .min(1)
        .regex(/^[a-z0-9-]+$/),
    weeks: z
        .array(z.object({
        week_no: z.number().int().min(1).max(52),
        topics: z
            .array(z.object({
            name: z.string().min(1),
            subtopics: z.array(z.string()).default([]),
        }))
            .default([]),
    }))
        .default([]),
});
export const ImportSyllabusInput = z.object({
    file: SyllabusFile,
    dry_run: z.boolean().default(false),
});
export const ImportSyllabusResponse = z.object({
    subject: Subject,
    topics_created: z.number().int().nonnegative(),
    topics_existing: z.number().int().nonnegative(),
    dry_run: z.boolean(),
});
/** Ask Claude to author questions for a topic. Output is always `pending`. */
export const GenerateQuestionsInput = z.object({
    topic_id: Id,
    count: z.number().int().min(1).max(20).default(5),
    type: QuestionType.optional(),
    difficulty: Difficulty.optional(),
    /** Extra syllabus context / style notes appended to the prompt. */
    notes: z.string().max(4000).optional(),
    /**
     * Contents of `content/prompts/generate-questions.md`. The Worker has no
     * filesystem, so the admin UI ships the template with the request; omitted,
     * the API falls back to its built-in prompt.
     */
    prompt_template: z.string().max(20_000).optional(),
});
export const GenerateQuestionsResponse = z.object({
    questions: z.array(Question),
    requested: z.number().int().nonnegative(),
    /** Model output that failed schema validation and was dropped. */
    rejected: z.array(z.object({ index: z.number().int(), message: z.string() })),
});
/* ------------------------------------------------------------------ */
/* exams (admin)                                                       */
/* ------------------------------------------------------------------ */
/** Exam row plus the derived counters every admin list needs. */
/**
 * Who an exam is for.
 *
 * An EMPTY list means every student — which is what every exam created before
 * assignment existed still is, so no backfill was needed and no exam silently
 * became invisible. Assigning even one student narrows it to the named list.
 */
export const SetExamStudentsInput = z.object({
    student_ids: z.array(Id).max(1000),
});
export const ExamStudentsResponse = z.object({
    student_ids: z.array(Id),
    /** True when the list is empty — stated rather than left to be inferred. */
    open_to_all: z.boolean(),
});
export const ExamSummary = Exam.extend({
    question_count: z.number().int().nonnegative(),
    total_marks: z.number(),
    attempt_status: AttemptStatus.nullable(),
    /**
     * How many students this exam is assigned to. ZERO MEANS EVERYONE, not
     * nobody — see `SetExamStudentsInput`. Optional so an older API build does
     * not fail the contract on a field it has never heard of.
     */
    assigned_count: z.number().int().nonnegative().optional(),
});
export const ExamListQuery = PageQuery.extend({
    status: ExamStatus.optional(),
    subject_id: Id.optional(),
});
export const ExamsResponse = z.object({
    exams: z.array(ExamSummary),
    total: z.number().int().nonnegative(),
});
/** An attached question, joined with the bank row, for the admin exam editor. */
export const ExamQuestionDetail = ExamQuestion.extend({
    question: QuestionWithContext,
});
export const ExamDetailResponse = z.object({
    exam: ExamSummary,
    questions: z.array(ExamQuestionDetail),
});
export const CreateExamInput = z.object({
    subject_id: Id,
    title: z.string().min(1).max(200),
    /** Defaults to `from_start`: duration counts from the student's own start. */
    timing_mode: ExamTimingMode.default("from_start"),
    scheduled_at: EpochMs.nullable().default(null),
    /** Optional start window. Null/omitted = no bound; permissive by design. */
    available_from: EpochMs.nullable().default(null),
    available_until: EpochMs.nullable().default(null),
    duration_min: z.number().int().positive().max(600),
    /**
     * Minutes from attempt start before a manual submit is allowed (default 0).
     * A real invigilated sitting sets e.g. 40. Must not exceed `duration_min`.
     */
    min_submit_after_min: z.number().int().nonnegative().max(600).default(0),
    /** Reveal the answer key + solutions on the student result page. Default true. */
    show_answers_after_submit: z.boolean().default(true),
    /**
     * Let the student see which topics this exam covers, before they sit it.
     *
     * Off by default. For a test of taught material, publishing the topics is
     * the point — it is what makes "revise for the exam" mean something. For a
     * diagnostic, announcing them changes the answer being measured, so this
     * has to be a deliberate choice per exam rather than a global default.
     */
    syllabus_visible: z.boolean().default(false),
    /**
     * Ask the student to photograph their rough sheets after submitting.
     *
     * Off by default: it suits a written paper, not the multiple-choice and
     * coding exams this runs, and it competes for the student's attention with
     * the recording upload that is still draining at that moment.
     */
    collect_answer_sheets: z.boolean().default(false),
    /**
     * Require a paired phone as the side camera. Off by default — it is a third
     * video stream on a connection that often cannot carry two.
     */
    require_phone: z.boolean().default(false),
    /** On-screen scratchpad for rough work. Default true; never uploaded. */
    allow_scratchpad: z.boolean().default(true),
    covers_weeks: z.array(z.number().int().min(1).max(52)).default([]),
});
export const UpdateExamInput = CreateExamInput.partial().refine((v) => Object.keys(v).length > 0, { message: "At least one field is required" });
export const ExamResponse = z.object({ exam: ExamSummary });
/** Attach questions to an exam. Omitted `order_idx` appends in array order. */
export const AttachQuestionsInput = z.object({
    questions: z
        .array(z.object({
        question_id: Id,
        marks: z.number().positive().max(100).default(1),
        negative_marks: z.number().nonnegative().max(100).default(0),
        order_idx: z.number().int().nonnegative().optional(),
    }))
        .min(1)
        .max(200),
    /** Replace the existing paper instead of appending. */
    replace: z.boolean().default(false),
});
export const ReorderExamQuestionsInput = z.object({
    question_ids: z.array(Id).min(1).max(200),
});
/**
 * Auto-pick rule (v1): 40% weak topics (topics the student got wrong before),
 * 40% newest covered weeks, 20% revision (older covered weeks). Buckets that
 * run dry spill into the next bucket in that order, so the paper always fills
 * if the bank has enough approved questions.
 */
export const AutoPickBucket = z.enum(["weak", "recent", "revision"]);
export const AutoPickInput = z.object({
    count: z.number().int().min(1).max(200),
    marks: z.number().positive().max(100).default(1),
    negative_marks: z.number().nonnegative().max(100).default(0),
    difficulty: Difficulty.optional(),
    type: QuestionType.optional(),
    /** Weights must sum to 1. Defaults encode the 40/40/20 rule. */
    weights: z
        .object({
        weak: z.number().min(0).max(1).default(0.4),
        recent: z.number().min(0).max(1).default(0.4),
        revision: z.number().min(0).max(1).default(0.2),
    })
        .default({ weak: 0.4, recent: 0.4, revision: 0.2 }),
    /**
     * Whose history defines a "weak" topic. Omitted — the default — means the
     * whole cohort's, which is what a paper several students will sit should be
     * built from. Name a student only when building a paper for them alone.
     */
    student_id: Id.optional(),
    /** Report the picks without writing exam_questions. */
    dry_run: z.boolean().default(false),
    /** Replace the current paper instead of appending. */
    replace: z.boolean().default(false),
});
export const AutoPickResponse = z.object({
    dry_run: z.boolean(),
    requested: z.number().int().nonnegative(),
    picked: z.array(z.object({
        question_id: Id,
        bucket: AutoPickBucket,
        topic_id: Id,
        week_no: z.number().int().nullable(),
        difficulty: Difficulty,
    })),
    /** Per-bucket target vs what the bank could actually supply. */
    shortfall: z.record(AutoPickBucket, z.number().int().nonnegative()),
});
export const PublishExamInput = z.object({
    /** Required when moving draft -> scheduled. */
    scheduled_at: EpochMs.nullable().default(null),
    status: z.enum(["scheduled", "live", "done", "draft"]).default("scheduled"),
});
/* ------------------------------------------------------------------ */
/* exams (student)                                                     */
/* ------------------------------------------------------------------ */
/** What the student sees on their dashboard. Never includes question data. */
export const StudentExamSummary = z.object({
    id: Id,
    title: z.string(),
    subject_name: z.string(),
    timing_mode: ExamTimingMode,
    scheduled_at: EpochMs.nullable(),
    duration_min: z.number().int().positive(),
    covers_weeks: z.array(z.number().int()),
    status: ExamStatus,
    question_count: z.number().int().nonnegative(),
    total_marks: z.number(),
    /**
     * The AVAILABILITY window — when the student may start — not the duration.
     * In `from_start` mode these are the optional `available_from`/`until`
     * bounds (null = unbounded). In `scheduled` mode they anchor to
     * `scheduled_at`. The countdown itself always runs `duration_min` from the
     * attempt's own start.
     */
    window_opens_at: EpochMs.nullable(),
    window_closes_at: EpochMs.nullable(),
    attempt: z
        .object({
        id: Id,
        status: AttemptStatus,
        started_at: EpochMs.nullable(),
        submitted_at: EpochMs.nullable(),
        score: z.number().nullable(),
        max_score: z.number().nullable(),
    })
        .nullable(),
    can_start: z.boolean(),
    /** Why `can_start` is false — shown verbatim in the UI. */
    blocked_reason: z.string().nullable(),
    /**
     * Topic names this paper covers, or null when the exam does not publish its
     * syllabus.
     *
     * Null and empty are different: null means "not published", empty means
     * "published, and the paper has no topics to name" — which is a content
     * problem worth seeing rather than hiding behind the same blank space.
     *
     * Derived from the questions actually on the paper, never stored, so it
     * cannot drift when the paper is edited. Names only: anything finer starts
     * telling the student where the marks are.
     */
    syllabus_topics: z.array(z.string()).nullable(),
});
export const MyExamsResponse = z.object({
    upcoming: z.array(StudentExamSummary),
    past: z.array(StudentExamSummary),
    server_now_ms: EpochMs,
});
/* ------------------------------------------------------------------ */
/* attempts (student)                                                  */
/* ------------------------------------------------------------------ */
export const StartAttemptInput = z.object({ exam_id: Id });
/** An answer as returned to the student: grading columns are withheld. */
export const StudentAnswer = Answer.omit({
    id: true,
    attempt_id: true,
    is_correct: true,
    marks_awarded: true,
});
/** Per-question paper metadata (marks live on exam_questions, not questions). */
export const AttemptPaperItem = z.object({
    order_idx: z.number().int().nonnegative(),
    marks: z.number(),
    negative_marks: z.number(),
    question: StudentQuestion,
    /**
     * CODE only: the checks, expectations included.
     *
     * They have to be — the code runs in the student's browser, which is the
     * only place it CAN run here, and a comparison needs something to compare
     * against. So a student who opens devtools can read what each test wants.
     * That is accepted rather than papered over: the mark is provisional, the
     * source is stored for re-running, and the screen recording shows the code
     * being written. See the note beside `CodeTest`.
     */
    tests: z.array(CodeTest).optional(),
});
export const ViolationCounts = z.record(ProctorEventType, z.number().int());
/** Live pairing/heartbeat view of both devices. */
export const DeviceStatus = z.object({
    role: DeviceRole,
    paired: z.boolean(),
    connected_at: EpochMs.nullable(),
    last_heartbeat_at: EpochMs.nullable(),
    /** last_heartbeat_at within PHONE_HEARTBEAT_TIMEOUT_MS of server_now_ms. */
    alive: z.boolean(),
});
/** How many photos of each kind the phone has uploaded for this attempt. */
export const MediaCounts = z.record(AttemptMediaKind, z.number().int());
/**
 * The single payload the laptop polls — timer, answers, marked-for-review
 * flags, media counts and device status in one call. It is polled every few
 * seconds for hours, so pass `?paper=0` once the paper is in hand and the
 * server will omit `paper` (the expensive part) from the response.
 *
 * `remaining_ms` is authoritative — it is computed from `started_at` on the
 * server; the client clock is never trusted and `server_now_ms` is provided
 * only for drift display.
 */
export const AttemptStateResponse = z.object({
    attempt: Attempt,
    exam: z.object({
        id: Id,
        title: z.string(),
        /** The exam's primary/display subject; per-question subject drives tabs. */
        subject_name: z.string(),
        duration_min: z.number().int().positive(),
        /** Submit-lock: minutes from start before manual submit is allowed. */
        min_submit_after_min: z.number().int().nonnegative(),
        total_marks: z.number(),
        /**
         * Whether the submitted screen should ask for rough-sheet photos. The
         * student page needs this to know whether to run the capture step at all,
         * and to decide whether the phone is put into `submitted_upload`.
         */
        collect_answer_sheets: z.boolean().default(false),
        /** Whether setup should ask for a phone at all. */
        require_phone: z.boolean().default(false),
        /** Whether the runner offers the rough-work scratchpad. */
        allow_scratchpad: z.boolean().default(true),
    }),
    /**
     * Absolute epoch ms when a manual submit becomes allowed
     * (`started_at + min_submit_after_min`). Null when unlocked (min is 0) or the
     * attempt has not started. The runner shows a countdown to this; enforcement
     * is server-side on submit.
     */
    submit_unlocks_at_ms: EpochMs.nullable(),
    /** Omitted when the request passed `?paper=0`. */
    paper: z.array(AttemptPaperItem).optional(),
    answers: z.array(StudentAnswer),
    server_now_ms: EpochMs,
    /** started_at + duration; null before the attempt starts. */
    deadline_ms: EpochMs.nullable(),
    remaining_ms: z.number().int().nonnegative(),
    /** Setup-flow stage the laptop has published to the phone. */
    stage: AttemptStage,
    devices: z.array(DeviceStatus),
    /** Lets the laptop stepper gate "Start" on steps performed on the phone. */
    media_counts: MediaCounts,
    violation_counts: ViolationCounts,
    total_violations: z.number().int().nonnegative(),
});
export const AttemptStateQuery = z.object({
    /** `?paper=0` omits the paper from the response. Default 1. */
    paper: z
        .enum(["0", "1"])
        .default("1")
        .transform((v) => v === "1"),
});
export const SaveAnswerResponse = z.object({
    answer: StudentAnswer,
    server_now_ms: EpochMs,
    remaining_ms: z.number().int().nonnegative(),
});
/** Result of scoring. Sent only after submit — never mid-attempt. */
export const AttemptResultResponse = z.object({
    attempt: Attempt,
    score: z.number(),
    max_score: z.number(),
    correct: z.number().int().nonnegative(),
    incorrect: z.number().int().nonnegative(),
    unanswered: z.number().int().nonnegative(),
    auto_submitted: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* result detail — the student's own performance page                  */
/* ------------------------------------------------------------------ */
/**
 * Everything the student's result page needs, ownership-gated to their OWN
 * attempt and only once it is (auto_)submitted. The reveal fields
 * (`correct_answer`, `solution_md`, `is_correct`) are present only when the
 * exam's `show_answers_after_submit` is true (`reveal_answers`); the performance
 * aggregates and each question's `marks_awarded` are always returned.
 */
export const ResultOverall = z.object({
    score: z.number(),
    max_score: z.number(),
    percent: z.number(),
    correct: z.number().int().nonnegative(),
    incorrect: z.number().int().nonnegative(),
    unanswered: z.number().int().nonnegative(),
});
export const ResultBySubject = z.object({
    subject_id: Id,
    subject_name: z.string(),
    correct: z.number().int().nonnegative(),
    answered: z.number().int().nonnegative(),
    marks_awarded: z.number(),
    max_marks: z.number(),
});
export const ResultByType = z.object({
    type: QuestionType,
    correct: z.number().int().nonnegative(),
    answered: z.number().int().nonnegative(),
});
export const ResultByTopic = z.object({
    topic_id: Id,
    topic_name: z.string(),
    subject_name: z.string(),
    week_no: z.number().int().nullable(),
    correct: z.number().int().nonnegative(),
    answered: z.number().int().nonnegative(),
});
export const ResultDetailQuestion = z.object({
    order_idx: z.number().int().nonnegative(),
    subject_id: Id,
    subject_name: z.string(),
    topic_name: z.string(),
    type: QuestionType,
    body_md: z.string(),
    options: z.array(QuestionOption),
    /** What the student submitted, or null if left blank. */
    response: AnswerResponse.nullable(),
    marks: z.number(),
    marks_awarded: z.number().nullable(),
    /** Reveal-gated: present only when reveal_answers is true. */
    correct_answer: CorrectAnswer.optional(),
    is_correct: z.boolean().nullable().optional(),
    solution_md: z.string().nullable().optional(),
    /**
     * Where to go and learn this, resolved server-side: the question's own link
     * if it has one, otherwise the topic's.
     *
     * Sent for every question, not only the wrong ones — a student who got it
     * right by luck should still be able to go and check, and hiding the link
     * behind correctness would mean the API's answer changed with the grade.
     * The page decides what to surface; the API decides what exists.
     */
    video_url: z.string().nullable().optional(),
    reading_url: z.string().nullable().optional(),
});
export const ResultDetailResponse = z.object({
    overall: ResultOverall,
    by_subject: z.array(ResultBySubject),
    by_type: z.array(ResultByType),
    by_topic: z.array(ResultByTopic),
    questions: z.array(ResultDetailQuestion),
    /** True when the exam permits showing the answer key + solutions. */
    reveal_answers: z.boolean(),
});
export const AttemptTimerResponse = z.object({
    attempt_id: Id,
    status: AttemptStatus,
    server_now_ms: EpochMs,
    deadline_ms: EpochMs.nullable(),
    remaining_ms: z.number().int().nonnegative(),
    auto_submitted: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* device pairing                                                      */
/* ------------------------------------------------------------------ */
/** How long a phone may be silent before PHONE_DISCONNECTED is recorded. */
export const PHONE_HEARTBEAT_TIMEOUT_MS = 45_000;
/** Both devices are expected to beat at this interval. */
export const HEARTBEAT_INTERVAL_MS = 15_000;
export const IssuePairCodeResponse = z.object({
    attempt_id: Id,
    pair_code: z.string(),
    /** Codes are single-use for the life of the attempt, not time-boxed in v1. */
    issued_at: EpochMs,
    phone: DeviceStatus,
});
/**
 * The phone is not signed in. Claiming a pair code returns a device token that
 * the phone sends as `X-Device-Token` on heartbeat, proctor and media calls.
 */
export const ClaimPairCodeResponse = z.object({
    attempt_id: Id,
    role: z.literal("phone"),
    device_token: z.string().min(1),
    exam_title: z.string(),
    student_name: z.string(),
    heartbeat_interval_ms: z.number().int().positive(),
});
export const HeartbeatInput = z.object({ role: DeviceRole });
/**
 * The phone's only channel. It beats every 15 s and renders whatever `stage`
 * the laptop has published — the laptop orchestrates the setup flow, the phone
 * never decides it.
 */
export const HeartbeatResponse = z.object({
    server_now_ms: EpochMs,
    attempt_status: AttemptStatus,
    remaining_ms: z.number().int().nonnegative(),
    /** Directive from the laptop: what this device should be doing now. */
    stage: AttemptStage,
    devices: z.array(DeviceStatus),
    media_counts: MediaCounts,
    heartbeat_interval_ms: z.number().int().positive(),
});
/** Laptop-only. Publishes the stage the phone should follow. */
export const SetStageInput = z.object({ stage: AttemptStage });
export const SetStageResponse = z.object({
    attempt_id: Id,
    stage: AttemptStage,
    server_now_ms: EpochMs,
});
export const DevicesResponse = z.object({
    devices: z.array(DeviceSession),
    server_now_ms: EpochMs,
});
/* ------------------------------------------------------------------ */
/* proctor                                                             */
/* ------------------------------------------------------------------ */
/**
 * v1 NEVER terminates an exam because of violations — the response exists so
 * the client can render a warning banner, nothing more.
 */
export const ProctorIngestResponse = z.object({
    accepted: z.number().int().nonnegative(),
    violation_counts: ViolationCounts,
    total_violations: z.number().int().nonnegative(),
    /** Always false in v1. Reserved so the client can already branch on it. */
    terminated: z.literal(false),
});
export const ProctorSummaryResponse = z.object({
    attempt_id: Id,
    violation_counts: ViolationCounts,
    total_violations: z.number().int().nonnegative(),
    last_event_at: EpochMs.nullable(),
});
export const ProctorTimelineResponse = z.object({
    attempt_id: Id,
    /** Chronological; `offset_ms` is relative to attempts.started_at. */
    events: z.array(ProctorEvent.extend({ offset_ms: z.number().int() })),
    violation_counts: ViolationCounts,
    total_violations: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------------ */
/* media                                                               */
/* ------------------------------------------------------------------ */
/** Query string for the chunk upload (body is the raw blob, not JSON). */
export const UploadChunkQuery = z.object({
    source: RecordingSource,
    chunk_index: z.coerce.number().int().nonnegative(),
    started_ms: z.coerce.number().int().nonnegative(),
    duration_ms: z.coerce.number().int().nonnegative().default(0),
    /**
     * Which MediaRecorder session this chunk came from.
     *
     * OPTIONAL, not defaulted: "omitted" and "session 0" have to stay
     * distinguishable. An upload is idempotent per chunk index, so a chunk
     * queued by an older build and retried after a reload would otherwise
     * overwrite a correctly-stored session with 0 and merge that chunk into the
     * wrong run. A new row with no value still lands as 0, from the column
     * default.
     */
    session_idx: z.coerce.number().int().nonnegative().optional(),
});
export const UploadChunkResponse = z.object({
    recording: Recording,
    /** true when this chunk_index already existed and the blob was overwritten. */
    replaced: z.boolean(),
});
export const UploadPhotoQuery = z.object({ kind: AttemptMediaKind });
export const UploadPhotoResponse = z.object({ media: AttemptMedia });
/** Default life of a playback manifest and of the URLs inside it. */
export const PLAYBACK_TTL_S = 6 * 60 * 60;
/**
 * One chunk, ready to play. `offset_ms` is measured from `attempts.started_at`
 * — the master timeline's t=0 — for **both** sources, so a phone that joined
 * 30 s late simply starts at `offset_ms: 30000` and the gap is visible.
 */
export const PlaybackChunk = z.object({
    chunk_index: z.number().int().nonnegative(),
    /** Runs are split on a change of this as well as on a gap in time. */
    session_idx: z.number().int().nonnegative().default(0),
    offset_ms: z.number().int(),
    duration_ms: z.number().int().nonnegative(),
    bytes: z.number().int().nonnegative(),
    r2_key: z.string(),
    /** Pre-signed; valid until the manifest's `expires_at`. */
    url: z.string().min(1),
});
/**
 * The manifest is WINDOWED. Signing a URL is HMAC work, and a 3-hour exam is
 * ~1080 chunks per source — signing them all in one Worker invocation risks the
 * 10 ms free-plan CPU cap. So the player asks for a window of chunks around the
 * playhead (`?from=<chunk_index>&limit=<n>`, applied per source) and pages via
 * the `next_from` cursor each source returns. Only the windowed chunks are
 * signed; the whole-attempt totals below are computed from a plain row read and
 * stay accurate for the scrubber.
 */
export const PLAYBACK_WINDOW_DEFAULT = 50;
export const PLAYBACK_WINDOW_MAX = 100;
export const PlaybackManifestQuery = z.object({
    /** First chunk_index to include, inclusive, per source. */
    from: z.coerce.number().int().nonnegative().default(0),
    /** Max chunks signed PER SOURCE this request. */
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(PLAYBACK_WINDOW_MAX)
        .default(PLAYBACK_WINDOW_DEFAULT),
});
/**
 * Everything the synced player needs for one WINDOW. `chunks` is the signed
 * slice `[from, from + limit)`; the `total_*` fields describe the whole
 * attempt, and `next_from` is the cursor for the next window (null when this
 * source is exhausted). `offset_ms` is absolute (from `attempts.started_at`),
 * so windows compose into one timeline. Refetch when `expires_at` approaches.
 */
export const PlaybackManifestResponse = z.object({
    attempt_id: Id,
    /** Timeline origin. Null only if the attempt never started. */
    started_at: EpochMs.nullable(),
    submitted_at: EpochMs.nullable(),
    /** Total attempt span, for the scrubber's width. */
    duration_ms: z.number().int().nonnegative(),
    expires_at: EpochMs,
    /** Echo of the window requested, so the client can confirm what it got. */
    window: z.object({
        from: z.number().int().nonnegative(),
        limit: z.number().int().positive(),
    }),
    sources: z.array(z.object({
        source: RecordingSource,
        /** First chunk's offset — how late this device joined (whole attempt). */
        first_offset_ms: z.number().int(),
        /** Whole-attempt aggregates (not just this window). */
        total_duration_ms: z.number().int().nonnegative(),
        total_bytes: z.number().int().nonnegative(),
        total_chunks: z.number().int().nonnegative(),
        /** True if chunks beyond this window exist for this source. */
        has_more: z.boolean(),
        /** Cursor for the next window, or null when exhausted. */
        next_from: z.number().int().nonnegative().nullable(),
        /** The signed slice [from, from + limit) for this source. */
        chunks: z.array(PlaybackChunk),
    })),
    photos: z.array(AttemptMedia.extend({
        offset_ms: z.number().int(),
        url: z.string().min(1),
    })),
});
/** Admin mints a short-lived HMAC token for one object key. */
export const SignPlaybackInput = z.object({
    r2_key: z.string().min(1).max(300),
    expires_in_s: z.number().int().min(30).max(86_400).default(3600),
});
export const SignPlaybackResponse = z.object({
    url: z.string().min(1),
    r2_key: z.string(),
    expires_at: EpochMs,
});
/* ------------------------------------------------------------------ */
/* admin review                                                        */
/* ------------------------------------------------------------------ */
export const AdminAttemptSummary = z.object({
    attempt: Attempt,
    exam_title: z.string(),
    student_name: z.string(),
    total_violations: z.number().int().nonnegative(),
});
export const AdminAttemptsQuery = PageQuery.extend({
    exam_id: Id.optional(),
    status: AttemptStatus.optional(),
});
export const AdminAttemptsResponse = z.object({
    attempts: z.array(AdminAttemptSummary),
    total: z.number().int().nonnegative(),
});
/** Full per-question review row — the only place correct answers are exposed. */
export const AdminAnswerDetail = z.object({
    order_idx: z.number().int().nonnegative(),
    marks: z.number(),
    negative_marks: z.number(),
    question: QuestionWithContext,
    response: Answer.shape.response,
    is_correct: z.boolean().nullable(),
    /** Effective marks — the override when one exists, otherwise the auto score. */
    marks_awarded: z.number().nullable(),
    /** What the scorer computed. Kept even when overridden, so both are visible. */
    auto_marks_awarded: z.number().nullable(),
    marks_override: z.number().nullable(),
    override_note: z.string().nullable(),
    time_spent_ms: z.number().int().nonnegative(),
    visit_count: z.number().int().nonnegative(),
    /**
     * Every moment the student opened this question, in ms from
     * `attempts.started_at` —
     * the same t=0 as the recording chunks. Click a question, seek the video.
     */
    visit_offsets_ms: VisitOffsets,
});
/** Manually correct one question's marks. Recomputes and stores attempts.score. */
export const OverrideMarksInput = z.object({
    question_id: Id,
    /** null clears the override and restores the auto-computed marks. */
    marks_awarded: z.number().min(-100).max(100).nullable(),
    note: z.string().max(1000).nullable().default(null),
});
export const OverrideMarksResponse = z.object({
    attempt: Attempt,
    answer: AdminAnswerDetail,
    score: z.number(),
    max_score: z.number(),
});
export const AdminAttemptDetailResponse = z.object({
    attempt: Attempt,
    exam_title: z.string(),
    subject_name: z.string(),
    student_name: z.string(),
    answers: z.array(AdminAnswerDetail),
    violation_counts: ViolationCounts,
    total_violations: z.number().int().nonnegative(),
    duration_ms: z.number().int().nonnegative(),
});
/**
 * Everything the synced player needs: both sources, chunks in order, each with
 * `offset_ms` from attempts.started_at so two tracks can be aligned.
 */
export const AdminMediaResponse = z.object({
    attempt_id: Id,
    started_at: EpochMs.nullable(),
    sources: z.array(z.object({
        source: RecordingSource,
        chunks: z.array(Recording.extend({ offset_ms: z.number().int() })),
        total_bytes: z.number().int().nonnegative(),
        total_duration_ms: z.number().int().nonnegative(),
    })),
    photos: z.array(AttemptMedia),
});
/* ------------------------------------------------------------------ */
/* admin analytics (v1.1 — the admin composes this client-side today)  */
/* ------------------------------------------------------------------ */
export const AnalyticsQuery = z.object({
    subject_id: Id.optional(),
    /** Only attempts submitted at or after this instant. */
    since_ms: z.coerce.number().int().nonnegative().optional(),
});
export const AnalyticsResponse = z.object({
    topic_accuracy: z.array(z.object({
        topic_id: Id,
        topic_name: z.string(),
        subject_id: Id,
        subject_name: z.string(),
        week_no: z.number().int().nullable(),
        answered: z.number().int().nonnegative(),
        correct: z.number().int().nonnegative(),
        accuracy: z.number().min(0).max(1),
    })),
    score_trend: z.array(z.object({
        attempt_id: Id,
        exam_id: Id,
        exam_title: z.string(),
        subject_id: Id,
        subject_name: z.string(),
        submitted_at: EpochMs,
        score: z.number(),
        max_score: z.number(),
        percent: z.number(),
    })),
    violation_history: z.array(z.object({
        attempt_id: Id,
        exam_title: z.string(),
        submitted_at: EpochMs.nullable(),
        total_violations: z.number().int().nonnegative(),
        violation_counts: ViolationCounts,
    })),
});
/* ------------------------------------------------------------------ */
/* admin — students                                                    */
/* ------------------------------------------------------------------ */
/**
 * There is no self-signup: an admin creates every account. That was fine when
 * the app served one student and the account was seeded; running general exams
 * means the roster changes between sittings, so it needs an API.
 *
 * A student is never deleted — their attempts, recordings and grades reference
 * them. `active: false` stops the sign-in and hides them from the pickers
 * while leaving every record intact.
 */
export const AdminUser = z.object({
    id: Id,
    email: z.string().email(),
    name: z.string(),
    role: UserRole,
    active: z.boolean(),
    /** Attempts they have started, so a roster shows who has actually sat one. */
    attempt_count: z.number().int().nonnegative(),
});
export const AdminUsersQuery = PageQuery.extend({
    role: UserRole.optional(),
    /**
     * Omitted = every account; the roster screen defaults to active only.
     *
     * NOT `z.coerce.boolean()`: that is `Boolean(value)`, so the string "false"
     * — which is exactly what a query string carries — parses as TRUE. It made
     * listing deactivated accounts impossible, i.e. made it impossible to find
     * and re-enable a student locked out on the morning of an exam.
     */
    active: z
        .enum(["true", "false"])
        .transform((v) => v === "true")
        .optional(),
    /** Case-insensitive substring of name or email. */
    q: z.string().max(200).optional(),
});
export const AdminUsersResponse = z.object({
    users: z.array(AdminUser),
    total: z.number().int().nonnegative(),
});
export const AdminUserResponse = z.object({ user: AdminUser });
/** The floor, not a policy — exam accounts are handed out, not chosen. */
export const MIN_PASSWORD_LENGTH = 8;
export const CreateUserInput = z.object({
    email: z.string().email().max(320),
    name: z.string().min(1).max(200),
    password: z.string().min(MIN_PASSWORD_LENGTH).max(200),
    role: UserRole.default("student"),
});
export const UpdateUserInput = z
    .object({
    email: z.string().email().max(320).optional(),
    name: z.string().min(1).max(200).optional(),
    /** Omit to leave the password alone; sending one resets it. */
    password: z.string().min(MIN_PASSWORD_LENGTH).max(200).optional(),
    active: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Nothing to update" });
/* ------------------------------------------------------------------ */
/* admin — live exam control                                           */
/* ------------------------------------------------------------------ */
/**
 * Every intervention carries a note. It is optional at the type level because
 * an urgent action must never be blocked by a text field, but the UI asks for
 * one each time and the audit row keeps whatever was given.
 */
const InterventionNote = z.string().max(1000).nullable().default(null);
/**
 * Minutes to add to (or, negative, take from) ONE attempt's clock. Bounded so
 * a mistyped value cannot silently turn a 60-minute paper into a 24-hour one.
 */
export const ExtendAttemptInput = z.object({
    minutes: z.number().int().min(-600).max(600).refine((m) => m !== 0, {
        message: "Extension cannot be zero",
    }),
    note: InterventionNote,
});
/**
 * The same, applied to the exam's own duration — every attempt on it, running
 * ones included, since the deadline is derived and never cached.
 */
export const ExtendExamInput = z.object({
    minutes: z.number().int().min(-600).max(600).refine((m) => m !== 0, {
        message: "Extension cannot be zero",
    }),
    /** Also push `available_until` out by the same amount, if the exam has one. */
    extend_window: z.boolean().default(true),
    note: InterventionNote,
});
/** End a running attempt now: grade what is there and mark it submitted. */
export const ForceSubmitInput = z.object({ note: InterventionNote });
/**
 * Put a submitted attempt back to in_progress — the student was cut off, or
 * submitted by accident. `extra_minutes` is what they get to finish in; without
 * it they resume against the original deadline, which has usually passed.
 */
export const ReopenAttemptInput = z.object({
    extra_minutes: z.number().int().min(0).max(600).default(0),
    note: InterventionNote,
});
/**
 * Wipe an attempt back to not_started: answers, visits, score, stage. The
 * recordings and proctor events of the discarded run are DELETED with it —
 * this is the "start over as if it never happened" action, which is why it is
 * separate from a retake.
 */
export const ResetAttemptInput = z.object({
    note: InterventionNote,
    /**
     * What the caller believes they are destroying. Optional so a script can
     * still act deliberately, but the admin UI always sends both: this endpoint
     * permanently deletes a sitting's answers, score, events and recordings, and
     * it is reached from a list that may have been open for an hour. A stale id
     * would otherwise wipe a different student's finished exam and return 200.
     */
    expect_student_id: Id.optional(),
    expect_status: AttemptStatus.optional(),
});
/**
 * Let a student sit an exam again on the SAME paper. Creates a fresh attempt
 * at `attempt_no + 1`; every earlier attempt stays exactly as it is.
 */
export const GrantRetakeInput = z.object({
    student_id: Id,
    note: InterventionNote,
});
/** One row of the intervention log shown on the attempt and exam screens. */
export const AdminActionEntry = z.object({
    id: Id,
    attempt_id: Id.nullable(),
    exam_id: Id.nullable(),
    actor_id: Id,
    actor_name: z.string(),
    action: AdminActionType,
    payload: z.record(z.string(), z.unknown()).nullable(),
    note: z.string().nullable(),
    ts_ms: EpochMs,
});
export const AdminActionsResponse = z.object({
    actions: z.array(AdminActionEntry),
});
/**
 * One row of the live monitor. Deliberately cheap: it is polled every few
 * seconds for every running attempt at once, so it carries counts and the
 * current position, never questions or media.
 */
export const LiveAttempt = z.object({
    attempt_id: Id,
    exam_id: Id,
    exam_title: z.string(),
    student_id: Id,
    student_name: z.string(),
    attempt_no: z.number().int().min(1),
    started_at: EpochMs.nullable(),
    /** Authoritative, from the server clock, including any extra time granted. */
    remaining_ms: z.number().int().nonnegative(),
    deadline_ms: EpochMs.nullable(),
    extra_time_min: z.number().int(),
    stage: AttemptStage,
    answered: z.number().int().nonnegative(),
    question_count: z.number().int().nonnegative(),
    /** The question they are on right now — the last one they opened. */
    current_order_idx: z.number().int().nonnegative().nullable(),
    devices: z.array(DeviceStatus),
    /** True when the laptop has missed enough heartbeats to count as gone. */
    offline: z.boolean(),
    rejoin_count: z.number().int().nonnegative(),
    offline_ms: z.number().int().nonnegative(),
    severity_counts: z.object({
        critical: z.number().int().nonnegative(),
        warn: z.number().int().nonnegative(),
        info: z.number().int().nonnegative(),
    }),
});
export const LiveAttemptsQuery = z.object({ exam_id: Id.optional() });
export const LiveAttemptsResponse = z.object({
    attempts: z.array(LiveAttempt),
    server_now_ms: EpochMs,
});
/* ------------------------------------------------------------------ */
/* admin — item analysis                                               */
/* ------------------------------------------------------------------ */
/**
 * How much the numbers below can be trusted, given how many people sat the
 * paper.
 *
 * Stated on every row rather than left to be inferred, because the whole
 * failure mode of item analysis is a confident-looking statistic computed from
 * four students. A discrimination index needs a cohort; below that it is
 * arithmetic, not evidence.
 */
export const StatConfidence = z.enum([
    /** Fewer than 5 responses. Reported, never acted on. */
    "insufficient",
    /** 5–14. Enough to raise a question, not enough to settle one. */
    "provisional",
    /** 15 or more. */
    "reliable",
]);
export const ITEM_STATS_PROVISIONAL_N = 5;
export const ITEM_STATS_RELIABLE_N = 15;
export function statConfidenceFor(n) {
    if (n >= ITEM_STATS_RELIABLE_N)
        return "reliable";
    if (n >= ITEM_STATS_PROVISIONAL_N)
        return "provisional";
    return "insufficient";
}
/**
 * What the response data says is wrong with a question.
 *
 * These are the reasons a paper misgrades people, in the order they matter.
 * `mis_keyed` is the one worth building the whole feature for: a question the
 * strong students get WRONG and the weak students get right is, far more often
 * than not, a question whose answer key is wrong — and no amount of reading it
 * over will reveal that, because the key looks right to whoever wrote it.
 */
export const ItemFlag = z.enum([
    "mis_keyed",
    "not_discriminating",
    "too_easy",
    "too_hard",
    "dead_distractor",
    "attractive_distractor",
    "often_skipped",
]);
export const ITEM_FLAG_LABEL = {
    mis_keyed: "Answer key may be wrong",
    not_discriminating: "Does not separate strong from weak",
    too_easy: "Almost everyone got it right",
    too_hard: "Almost nobody got it right",
    dead_distractor: "An option nobody chose",
    attractive_distractor: "A wrong option is pulling in strong students",
    often_skipped: "Frequently left unanswered",
};
/** One option's pull, and who it pulled. */
export const OptionBreakdown = z.object({
    key: z.string(),
    text: z.string(),
    is_correct: z.boolean(),
    chosen: z.number().int().nonnegative(),
    /** Share of those who answered. 0..1 */
    share: z.number().min(0).max(1),
    /**
     * Mean overall percentage of the students who chose this option, or null
     * when nobody did. The number that exposes an ambiguous question: if a wrong
     * option's choosers outscore the right option's, the question is not
     * measuring what it claims to.
     */
    mean_score_pct: z.number().nullable(),
});
export const ItemStats = z.object({
    question_id: Id,
    exam_id: Id,
    order_idx: z.number().int().nonnegative(),
    type: QuestionType,
    /** First line of the stem, for a table row. */
    summary: z.string(),
    topic_name: z.string(),
    /** Sat the exam and were shown this question. */
    presented: z.number().int().nonnegative(),
    /** Gave any response at all. */
    answered: z.number().int().nonnegative(),
    correct: z.number().int().nonnegative(),
    /**
     * Difficulty index: correct / answered. HIGH means EASY — the convention is
     * counter-intuitive and is spelled out wherever it is shown.
     */
    difficulty: z.number().min(0).max(1).nullable(),
    /** Share of those presented who left it blank. 0..1 */
    skip_rate: z.number().min(0).max(1),
    /**
     * Corrected point-biserial correlation between getting this item right and
     * the total score on the rest of the paper. −1..1.
     *
     * Corrected — the item's own marks are removed from the total — because
     * otherwise every item correlates with itself, and on a short paper that
     * inflates the figure enough to hide a genuinely bad question.
     */
    discrimination: z.number().min(-1).max(1).nullable(),
    /** Mean seconds spent, and how that compares with the paper's average. */
    mean_time_ms: z.number().nullable(),
    options: z.array(OptionBreakdown),
    flags: z.array(ItemFlag),
    confidence: StatConfidence,
});
export const ItemAnalysisQuery = z.object({
    exam_id: Id.optional(),
    /** Only questions carrying at least one flag. */
    flagged_only: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
});
export const ItemAnalysisResponse = z.object({
    items: z.array(ItemStats),
    /** Attempts the analysis is computed from — graded sittings only. */
    cohort_size: z.number().int().nonnegative(),
    /**
     * Reliability of the paper as a whole (KR-20 / Cronbach's alpha), or null
     * when the cohort is too small to compute one. Roughly: how consistently the
     * questions measure the same thing. Below 0.5 the total score is close to
     * noise; above 0.8 is a paper you can defend.
     */
    reliability: z.number().nullable(),
    exam_id: Id.nullable(),
    exam_title: z.string().nullable(),
});
/* ------------------------------------------------------------------ */
/* admin — recording coverage                                          */
/* ------------------------------------------------------------------ */
export const CoverageVerdict = z.enum(["complete", "partial", "poor", "none"]);
/**
 * How much of the exam each camera actually recorded.
 *
 * A proctored exam whose proctoring covered a third of it is not a proctored
 * exam — and until this existed the only way to find that out was to scrub to
 * the end of a video and notice it had stopped early. One attempt uploaded
 * 4:17 of a 12:11 sitting and looked completely normal on every screen.
 *
 * Measured over `[0, elapsed]`. The phone records from pairing, so it has
 * footage before the exam; that is worth having but it is not coverage of the
 * exam, and counting it would let a long setup mask a bad recording.
 */
export const SourceCoverage = z.object({
    source: RecordingSource,
    covered_ms: z.number().int().nonnegative(),
    /** 0..1, or null when the attempt never ran. */
    coverage: z.number().min(0).max(1).nullable(),
    longest_gap_ms: z.number().int().nonnegative(),
    verdict: CoverageVerdict,
});
export const AttemptCoverage = z.object({
    attempt_id: Id,
    elapsed_ms: z.number().int().nonnegative(),
    sources: z.array(SourceCoverage),
    /** The weakest source — an attempt is only as proctored as its worst camera. */
    worst: CoverageVerdict,
});
/* ------------------------------------------------------------------ */
/* admin — review triage                                               */
/* ------------------------------------------------------------------ */
/**
 * Why an attempt is worth watching.
 *
 * Every one of these is a REASON TO LOOK, never a finding. The distinction is
 * the whole design: this screen ranks a queue, it does not accuse anybody, and
 * an admin who cannot see what produced a ranking will rightly not trust it.
 * So there is no opaque score — each contribution is named, quantified, and
 * shown.
 */
export const ReviewReasonKind = z.enum([
    "critical_events",
    "warn_events",
    "recording_gap",
    "dropped_out",
    "fast_correct",
]);
export const REVIEW_REASON_LABEL = {
    critical_events: "Critical proctor events",
    warn_events: "Warnings",
    recording_gap: "Recording did not cover the exam",
    dropped_out: "Dropped out and rejoined",
    fast_correct: "Answered correctly unusually fast",
};
export const ReviewReason = z.object({
    kind: ReviewReasonKind,
    /** Plain sentence naming the evidence, e.g. "webcam covered 35%". */
    detail: z.string(),
    /** What this contributed to the ranking. Shown, so the order is auditable. */
    weight: z.number(),
});
export const ReviewCandidate = z.object({
    attempt_id: Id,
    exam_id: Id,
    exam_title: z.string(),
    student_id: Id,
    student_name: z.string(),
    attempt_no: z.number().int().min(1),
    submitted_at: EpochMs.nullable(),
    score: z.number().nullable(),
    max_score: z.number().nullable(),
    /** Sum of the reasons' weights. Meaningless alone; always shown with them. */
    priority: z.number(),
    reasons: z.array(ReviewReason),
    coverage: AttemptCoverage.nullable(),
});
export const ReviewQueueQuery = z.object({
    exam_id: Id.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const ReviewTriageResponse = z.object({
    candidates: z.array(ReviewCandidate),
    /** Graded attempts considered, so "3 of 40 worth a look" can be said. */
    considered: z.number().int().nonnegative(),
    /**
     * How many carried a reason, BEFORE `limit` truncated the list.
     *
     * `candidates.length` is post-slice, so presenting it as the count says
     * "50 of 400 worth a look" when 60 were flagged — a capped numerator read as
     * a total.
     */
    flagged: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------------ */
/* student — my progress                                              */
/* ------------------------------------------------------------------ */
/**
 * What a student can see about their own record.
 *
 * The student side has only ever shown ONE exam at a time: the result page for
 * the paper just submitted. So the question a student actually asks — "am I
 * getting better, and what should I work on?" — had no answer anywhere in the
 * app.
 *
 * Everything here is scoped to the signed-in student by the route, and carries
 * no answer keys and no other student's identity. The one cohort figure is an
 * anonymous mean, withheld below a cohort size where it would identify
 * somebody.
 */
export const MyAttemptRow = z.object({
    attempt_id: Id,
    exam_id: Id,
    exam_title: z.string(),
    subject_name: z.string(),
    attempt_no: z.number().int().min(1),
    submitted_at: EpochMs,
    score: z.number(),
    max_score: z.number(),
    /** 0..100, the comparable figure — papers differ in total marks. */
    percent: z.number(),
    time_taken_ms: z.number().int().nonnegative().nullable(),
    /** Duration plus any extra time granted, so an accommodation is not read
     *  as an overrun. */
    time_allowed_ms: z.number().int().nonnegative(),
    /**
     * The cohort's mean percentage on the same paper, or null when too few
     * people have sat it for a mean to be anonymous.
     */
    cohort_mean_percent: z.number().nullable(),
    cohort_size: z.number().int().nonnegative(),
});
/** Below this, a "class average" is one or two identifiable people. */
export const COHORT_MEAN_MIN = 3;
export const MyTopicRow = z.object({
    topic_id: Id,
    topic_name: z.string(),
    subject_name: z.string(),
    answered: z.number().int().nonnegative(),
    correct: z.number().int().nonnegative(),
    /** 0..1 */
    accuracy: z.number().min(0).max(1),
    /** Mean seconds per question here, for spotting where time goes. */
    mean_time_ms: z.number().int().nonnegative(),
});
/** Below this many answered questions, a topic accuracy is not worth acting on. */
export const TOPIC_ADVICE_MIN = 3;
export const MyProgressResponse = z.object({
    attempts: z.array(MyAttemptRow),
    topics: z.array(MyTopicRow),
    /**
     * The weakest topics with enough answered questions to mean something —
     * what to revise, rather than every topic sorted by a number.
     */
    revise: z.array(MyTopicRow),
    exams_sat: z.number().int().nonnegative(),
    mean_percent: z.number().nullable(),
    best_percent: z.number().nullable(),
});
//# sourceMappingURL=contracts.js.map